import { ApplicationGroup } from '@amzn/awscat-portfolio-assessment-types';
import { Autosuggest, AutosuggestProps, Box, Checkbox, Container, Header, Select, SelectProps, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { TimelineItem } from 'vis-timeline';

import { constants } from '../../../common/Localization';
import { GROUP_SORTING_OPTIONS,GroupSortingOptions } from './constants';

interface Props {
    groups: ApplicationGroup[];
    loading: boolean;
}

function GroupsPanel({ groups, loading }: Props) {
    const [ assignedGroups, setAssignedGroups ] = useState<ApplicationGroup[]>([]);
    const [ unassignedGroups, setUnassignedGroups ] = useState<ApplicationGroup[]>([]);
    const [ showAssignedGroups, setShowAssignedGroups ] = useState(false);
    const [ keyword, setKeyword ] = useState('');
    const [ sortOrder, setSortOrder ] = useState<SelectProps.Option>(GROUP_SORTING_OPTIONS[0]);

    useEffect(() => {
        const keywordInLowerCase = keyword.toLowerCase();
        const groupsWithKeyword = keyword ? groups.filter(group => group.name.toLowerCase().includes(keywordInLowerCase)) : groups;
        const sortByField = sortOrder.value || '';
        const sortByNameAsc = (a: ApplicationGroup, b: ApplicationGroup): number => a.name < b.name ? -1 : 1;
        const sortByRankingAsc = (a: ApplicationGroup, b: ApplicationGroup): number => {
            if (!a.ranking || !b.ranking) {
                return sortByNameAsc(a, b);
            }
            return (b.ranking || 0) - (a.ranking || 0);
        };
        const sortFunction = (a: ApplicationGroup, b: ApplicationGroup): number => {
            switch (sortByField) {
                case GroupSortingOptions.NAME_ASC:
                    return sortByNameAsc(a, b);
                case GroupSortingOptions.NAME_DESC:
                    return -sortByNameAsc(a, b);
                case GroupSortingOptions.RANKING_ASC:
                    if (!a.ranking || !b.ranking) {
                        return sortByNameAsc(a, b);
                    }
                    return sortByRankingAsc(a, b);
                case GroupSortingOptions.RANKING_DESC:
                    if (!a.ranking || !b.ranking) {
                        return sortByNameAsc(a, b);
                    }
                    return -sortByRankingAsc(a, b);
                default:
                    return 1;
            }
        };
        groupsWithKeyword.sort(sortFunction);
        setAssignedGroups(groupsWithKeyword.filter(group => group.migrationWaveId));
        setUnassignedGroups(groupsWithKeyword.filter(group => !group.migrationWaveId));
    }, [groups, keyword, sortOrder]);

    function handleDragStart(event: React.DragEvent, group: ApplicationGroup) {
        event.dataTransfer.effectAllowed = 'move';
        const item: TimelineItem = {
            id: group.applicationGroupId,
            content: group.name,
            type: 'range',
        } as TimelineItem;
        event.dataTransfer.setData('text', JSON.stringify(item));
    }

    function getGroupsList(groups: ApplicationGroup[]) {
        return groups.map(group => {
            const pct = `${group.avgDataCollectedPct}%` || '0%';
            return (
                <li
                    key={group.applicationGroupId}
                    className='item'
                    draggable
                    onDragStart={e => handleDragStart(e, group)}>
                    <div className='group-title'>{group.name}</div>
                    <div className='group-progress-wrapper'>
                        <div className='progress' style={{width: pct}}></div>
                        <label className='progress-label'>{`${pct} ${constants.PASSED_VALIDATION}`}</label>
                    </div>
                </li>
            );
        });
    }

    const searchOptions: AutosuggestProps.OptionGroup[] = [{
        label: constants.UNASSIGNED_GROUPS,
        options: unassignedGroups.map(group => ({
            value: group.name,
        })),
    }, {
        label: constants.ASSIGNED_GROUPS,
        options: assignedGroups.map(group => ({
            value: group.name,
        })),
    }];
    const unassignedGroupsList = getGroupsList(unassignedGroups);
    const assignedGroupsList = getGroupsList(assignedGroups);
    return (
        <Container
            header={<Header variant='h2' actions={
                <SpaceBetween size='l' direction='horizontal'>
                    <Autosuggest
                        onChange={e => setKeyword(e.detail.value)}
                        options={searchOptions}
                        value={keyword}
                        placeholder={constants.SEARCH}
                        enteredTextLabel={value => value}
                    />
                    <Select onChange={e => setSortOrder(e.detail.selectedOption)} selectedOption={sortOrder} options={GROUP_SORTING_OPTIONS}></Select>
                    <Checkbox checked={showAssignedGroups} onChange={e => setShowAssignedGroups(e.detail.checked)}>{constants.SHOW_ASSIGNED_GROUPS}</Checkbox>
                </SpaceBetween>
            }>{constants.ASSIGN_APPLICATION_GROUPS}</Header>}>
            { loading ? <Spinner size='big' /> : (
                <Box className='wave-timeline-items-panel'>
                    <Box>
                        <h4>{constants.UNASSIGNED_GROUPS}: {unassignedGroupsList.length}</h4>
                        <ul className='items'>{unassignedGroupsList}</ul>
                    </Box>
                    { showAssignedGroups ? (
                        <Box>
                            <h4>{constants.ASSIGNED_GROUPS}: {assignedGroupsList.length}</h4>
                            <ul className='items'>{assignedGroupsList}</ul>
                        </Box>
                    )
                        : null }
                </Box>
            )}
        </Container>
    );
}

export default GroupsPanel;
